import React, {useEffect} from 'react';
import {createTheme, CssBaseline, StyledEngineProvider, ThemeProvider,} from "@mui/material";
import {RecoilRoot, useRecoilState, useSetRecoilState} from "recoil";
import {DialogProvider} from "./DialogProvider";
import {BrowserRouter, Navigate, Route, Routes, useParams} from "react-router-dom";
import {ApolloApp} from "./ApolloApp";
import {CurrentUserState, saveToStorage, UserType} from "./atoms/CurrentUser";
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns as DateFnsAdapter} from '@mui/x-date-pickers/AdapterDateFns';

import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";
import weekOfYear from "dayjs/plugin/weekOfYear"
import relativeTime from "dayjs/plugin/relativeTime"
import {SavingProvider} from './SavingProvider';
const GraphiqlPage = React.lazy(() => import("./pages/admin/GraphiqlPage").then(m => ({default: m.GraphiqlPage})));
import {useLocation} from "react-router";
import {Provider as JotaiProvider} from "jotai";
import {auditChangeStore} from "./atoms/ActiveAuditChange";

dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(relativeTime);

function App() {
   // The padding is due to : https://material-ui.com/components/grid/#limitations
   return (
      <StyledEngineProvider injectFirst>
         <ThemeProvider theme={theme}>
            <LocalizationProvider
               dateAdapter={DateFnsAdapter}
            >
               <div>
                  <CssBaseline/>
                  <RecoilRoot>
                     <JotaiProvider store={auditChangeStore}>
                        <BrowserRouter basename={"/"}>
                           <DialogProvider>
                              <SavingProvider>
                                 <AutoLoginApp/>
                              </SavingProvider>
                           </DialogProvider>
                        </BrowserRouter>
                     </JotaiProvider>
                  </RecoilRoot>
               </div>
            </LocalizationProvider>
         </ThemeProvider>
      </StyledEngineProvider>
   );
}

export const AutoLoginApp = () => {
   const location = useLocation();
   const setCurrentUser = useSetRecoilState(CurrentUserState);

   useEffect(() => {
      // Parse the `login` token only when the search string changes
      const params = new URLSearchParams(location.search);
      const token = params.get('login');

      if (!token) {
         return;
      }

      // Decode token and build user
      const tokenData = parseJwt(token) as {
         brandHouseId?: string;
         logisticsPartnerId?: string;
         userId?: string;
         accessToken?: string;
         accessTokenExpiresAt?: string;
         refreshToken?: string;
         refreshTokenExpiresAt?: string;
      };

      const newUser = {
         token,
         brandhouse: true,
         brandHouseId: tokenData.brandHouseId,
         userType: 'brandhouse' as UserType,
         userEmail: 'Brand',
      };

      if(tokenData.userId) {
         newUser.userType = 'user'
         newUser.userEmail = 'User'
      }

      // Set state and persist
      setCurrentUser(newUser);
      saveToStorage(newUser);

      // Remove only the `login` param from the URL
      params.delete('login');
      const newSearch = params.toString();
      const newUrl =
         location.pathname +
         (newSearch ? `?${newSearch}` : '') +
         location.hash;

      window.history.replaceState(null, '', newUrl);
   }, [location.search, location.pathname, location.hash, setCurrentUser]);

   return <Routes>
      <Route path={"/admin/graphql"} element={<React.Suspense fallback={<div>Loading...</div>}><GraphiqlPage/></React.Suspense>}/>
      <Route path={"login/:token"} element={<AutoLoginBrandHouse/>}/>
      <Route path={"redirect/:token/*"} element={<AutoLoginBrandHouse/>}/>
      <Route path={"*"} element={<ApolloApp/>}/>
   </Routes>
}

export function parseJwt(token: string) {
   var base64Url = token.split('.')[1];
   var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
   var jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
   }).join(''));
   return JSON.parse(jsonPayload);
}

const AutoLoginBrandHouse = () => {
   const {token, "*": page} = useParams<{ token: string, "*"?: string }>()
   const [currentUser, setCurrentUser] = useRecoilState(CurrentUserState);

   useEffect(() => {
      const tokenData = parseJwt(token);

      const newUser = {
         token: token,
         brandhouse: true,
         brandHouseId: tokenData.brandHouseId,
         shopId: tokenData.shopId,
         userType: 'brandhouse' as UserType,
         userEmail: 'Brand'
      };
      setCurrentUser(newUser);
      saveToStorage(newUser);
   }, [setCurrentUser, token])

   if (page) {
      return <Navigate to={"/" + page}/>
   } else if (currentUser) {
      return <Navigate to={"/brands/" + currentUser.brandHouseId + "/dashboard"}/>
   } else {
      return <div>Please wait while logging in...</div>
   }
}

export const theme = createTheme({
   components: {
      MuiButton: {
         defaultProps: {
            disableElevation: true
         },
         styleOverrides: {
            text: {
               textTransform: "none",
               fontWeight: 600
            }
         }
      },
      MuiPaper: {
         defaultProps: {
            variant: "outlined"
         },
         styleOverrides: {
            root: {
               borderRadius: '4px !important'
            }
         }
      },
      MuiDialogTitle: {
         styleOverrides: {
            root: {
               color: '#000000',
               fontWeight: 800
            }
         }
      },
      MuiDialogContent: {
         styleOverrides: {
            root: {
               color: '#606060'
            }
         }
      },
      MuiAccordion: {
         styleOverrides: {
            root: {
               borderRadius: '4px',
            }
         }
      },
      MuiChip: {
         styleOverrides: {
            root: {
               paddingTop: '15px',
               paddingBottom: '15px',
               paddingLeft: '5px',
               paddingRight: '5px',
               borderRadius: '8px'
            }
         }
      }
   },
   typography: {
      fontFamily: 'Roboto, sans-serif',
      h1: {
         letterSpacing: '0.3px',
         fontSize: '20px',
         color: '#606060',
         fontWeight: 600
      },
      h2: {
         letterSpacing: '0.24px',
         fontSize: '16px',
         fontWeight: 500,
         color: '#606060',
         marginBottom: '5px',
         marginTop: '20px'
      },
      h3: {
         letterSpacing: '0.16px',
         fontSize: '14px',
      },
      h4: {
         letterSpacing: '0.3px',
         fontSize: '20px',
         color: '#606060',
         fontWeight: 600
      },
      body1: {
         fontSize: '14px'
      },
      body2: {},
      subtitle1: {
         color: '#909090'
      },
      subtitle2: {
         color: '#909090',
         fontWeight: 'normal'
      }
   },
   palette: {
      background: {
         default: "#f1f1f1",
         paper: "#ffffff"
      },
      secondary: {
         main: "#808080",
         contrastText: "#FFFFFF"
      },
      primary: {main: "#FF5376"},
      success: {
         main: "#a5d6a7",
         contrastText: "#FFFFFF"
      }
   }
});

export default App;
